import moment from "moment";
import {
  SubscriptionPaymentMethodType,
  SubscriptionPlanDisplay,
  SubscriptionPlanType,
} from "@shared/types/settings.types";
import {
  Platform,
  RequestSource,
  UserImpersonationPermissionLevel,
  UtmParams,
} from "@shared/types/common";
import { ApiResponse } from "@shared/types/requests.types";

export interface UserProperties {
  id: number;
  username: string;
  email: string;
  feature_flags?: string[];
  expiration_timestamp?: number | null;
  original_expiration_timestamp?: number | null;
  full_name: string;
  image: string | null;
  uid: number;
  package: SubscriptionPlanDisplay;
  subscription_type: SubscriptionPlanDisplay;
  subscription_plan_type: SubscriptionPlanType;
  last_payment_subscription_type: SubscriptionPlanType;
  has_two_factor_auth: boolean;
  has_auto_renewal: boolean;
  is_trial: boolean;
  is_admin: boolean;
  is_telegram_connected: boolean;
  is_discord_connected: boolean;
  telegram_id: string;
  discord_id: string;
  discord_username: string;
  discord_bot_url: string;
  user_uuid: string;
  has_clients_connected: boolean;
  is_email_verified: boolean;
  bot_url: string;
  first_name: string;
  last_name: string;
  registered_to_app: boolean;
  should_track: boolean;
  user_hash: string;
  uuid_user_hash: string;
  is_onboarding: boolean;
  is_mobile_app_user: boolean;
  registration_timestamp: number;
  user_auto_trading_enabled: boolean;
  package_duration: number | null;
  labels: number[];
  has_groups: boolean | null;
  has_active_dca_bots: boolean;
  accepted_terms: boolean;
  accepted_affiliation_terms: boolean;
  has_valid_clients_connected: boolean;
  should_display_nps: boolean;
  should_display_web_nps: boolean;
  in_view_only_grace_period: boolean;
  user_legacy_auto_trading_enabled?: boolean;
  permission_level?: number;
  last_payment_option?: SubscriptionPaymentMethodType;
  is_sub_user?: boolean;
  asset_manager_id?: number;
  owner_subscription_plan_type?: SubscriptionPlanType | null;
  is_beta?: boolean;
  is_whop_user?: boolean;
  has_connected_whop_groups?: boolean;
  is_whop_creator?: boolean;
  sub_user_permission_level?: UserImpersonationPermissionLevel;
  is_whop_cornix_product?: boolean;
  has_non_deleted_clients_connected?: boolean;
  has_old_tradingview_bots?: boolean;
}

export interface AuthUser extends UserProperties {}

export class AuthUser {
  constructor(init: UserProperties) {
    Object.assign(this, init);
  }

  hasFeatureFlag(featureFlag: string) {
    return this.feature_flags?.includes(featureFlag);
  }

  isExpired() {
    if (!this.expiration_timestamp) return false;
    let compareTo = moment().unix();
    if (this.has_auto_renewal) {
      compareTo = moment().subtract(1, "day").unix();
    }
    return this.expiration_timestamp < compareTo;
  }

  isPro() {
    return [SubscriptionPlanType.PRO, SubscriptionPlanType.PREMIUM].includes(
      this.subscription_plan_type
    );
  }

  isMaxSubscription() {
    return (
      this.subscription_plan_type === SubscriptionPlanType.ASSET_MANAGER ||
      this.subscription_plan_type === SubscriptionPlanType.COMMUNITY_ADMIN
    );
  }

  hasExternalAccount() {
    return this.is_telegram_connected || this.is_discord_connected;
  }

  isFree() {
    return this.subscription_plan_type === SubscriptionPlanType.FREE;
  }

  getPackageDisplay() {
    if (this.is_sub_user) return "Sub Account";
    return `${this.is_trial ? "Trial - " : ""}${this.package}`;
  }

  isPurchaseSubscription() {
    return this.isFree() || this.isExpired() || this.is_trial;
  }

  isExtendSubscription() {
    return !this.isPurchaseSubscription();
  }

  isApplePaySubscription() {
    return (
      this.last_payment_option ===
        SubscriptionPaymentMethodType.APPLEPAY_SUBSCRIPTION && !this.isExpired()
    );
  }

  isGooglePaySubscription() {
    return (
      this.last_payment_option ===
        SubscriptionPaymentMethodType.GOOGLEPAY_SUBSCRIPTION &&
      !this.isExpired()
    );
  }

  isAssetManager() {
    return this.subscription_plan_type === SubscriptionPlanType.ASSET_MANAGER;
  }

  isNativeSubscription() {
    return this?.isApplePaySubscription() || this?.isGooglePaySubscription();
  }

  isRestrictedWhopUser() {
    if (this.is_whop_creator) {
      return !this.has_connected_whop_groups;
    } else if (this.is_whop_user) {
      return (
        !this.has_non_deleted_clients_connected &&
        !this.has_connected_whop_groups &&
        !this.is_whop_cornix_product
      );
    }
  }

  isWhopExperience() {
    return this.is_whop_user && !this.is_whop_cornix_product;
  }
}

export interface LoginRequest {
  email: string;
  password: string;
  two_factor_token?: string;
  captcha_token: string;
  is_whitelabel: boolean;
  remember_me?: boolean;
  mobile_identifier?: string;
  request_source: RequestSource;
  new_ip_token?: string;
  unique_identifier?: string;
  utm_user_uuid?: string;
}

export interface OAuthUserRequest {
  code: string;
  oauth_platform: OAuthPlatformType;
  captcha_token: string;
  is_whitelabel: boolean;
  two_factor_token?: string;
  new_ip_token?: string;
  unique_identifier?: string;
  mobile_identifier?: string;
  utm_user_uuid?: string;
  remember_me?: boolean;
}

export interface GetLoginAuthUrlRequest {
  oauth_platform: OAuthPlatformType;
  request_source?: RequestSource;
  referral_code?: string;
  client_token?: string;
  link_account?: boolean;
}

export interface ConnectDiscordOAuthRequest {
  code: string;
}

export interface LoginResponse {
  token: string;
  refresh_token: string;
  challenge?: LoginChallenge;
}

export interface LoginResponseWithUserId extends LoginResponse {
  user_id: number;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  token: string;
}

export type OAuthAuthorizationDecision = "approve" | "deny";

export interface OAuthAuthorizeRequest {
  url: string;
  decision?: OAuthAuthorizationDecision;
}

export interface OAuthAuthorizeResponse {
  client_name?: string;
  redirect_uri?: string;
  scope?: string;
  redirect_url?: string;
}

export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  campaign_id?: string;
  referral_code?: string;
  request_source: RequestSource;
  captcha_token?: string;
  is_whitelabel: boolean;
  utm_user_uuid?: string;
  client_token?: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

export interface ChangeUserFullNameRequest {
  first_name: string;
  last_name: string;
}

export interface ForgotPasswordRequest {
  email: string;
  request_source: RequestSource;
  captcha_token?: string;
  is_whitelabel: boolean;
}

export interface GenerateQRCodeRequest {
  refresh_token?: string;
}

export interface GenerateQRCodeResponse {
  qrcode: string;
  setup_key: string;
}

export interface DisableTwoFARequest {
  two_factor_token: string;
}

export type StoreTwoFARequest = Pick<GenerateQRCodeRequest, "refresh_token"> &
  Pick<DisableTwoFARequest, "two_factor_token">;

export interface GetCaptchaResponse {
  challenge: string;
  gt: string;
  success: number;
}

export interface GetCaptchaRequest {
  is_whitelabel: boolean;
}

export interface ValidateCaptchaRequest {
  challenge: string;
  validate: string;
  seccode: string;
  is_whitelabel: boolean;
}

export interface ReferralRequest {
  referral_code: string;
}

export interface DeleteCornixAccountConfirmationEmailRequest {
  email: string;
  request_source: RequestSource;
}

export interface ValidateCaptchaResponse {
  success: number;
  captcha_token?: string;
}

export type UserBasicDetails = Pick<
  UserProperties,
  "first_name" | "last_name" | "username" | "image"
>;

export type StorageOauthUserData = UserBasicDetails & { uuid: string };
export type StorageOAuthData = { [userId in number]: StorageOauthUserData } & {
  [userEmail in string]: StorageOauthUserData;
};

export interface VerifyConfirmEmailCodeRequest {
  auth_code: number;
  email_validator_uuid?: string;
  request_source?: RequestSource;
  mobile_identifier?: string;
  unique_identifier?: string;
}

export interface ResendRegistrationEmailRequest {
  email?: string;
  email_validator_uuid?: string;
  request_source: RequestSource;
  captcha_token?: string;
  is_whitelabel: boolean;
}

export type ConnectToTelegramRequest = { auth_code: string } & (
  | { user_short_uuid: string }
  | { uuid: string }
);

export enum UserSource {
  TELEGRAM = "telegram",
  DISCORD = "discord",
  DIRECT = "direct",
}

export enum ExternalAccountType {
  TELEGRAM = "Telegram",
  DISCORD = "Discord",
}

export enum OAuthPlatformType {
  GOOGLE = 1,
  APPLE = 2,
  FACEBOOK = 3,
  TELEGRAM = 4,
  DISCORD = 5,
}

export interface VerifyPersonalDetailsRequest {
  uuid: string;
  first_name: string;
  last_name: string;
  username: string;
  image: string;
}

export type SendConnectToTelegramAuthCodeRequest = {
  platform: Platform;
} & ({ user_short_uuid: string } | { uuid: string });

export enum LoginChallenge {
  TWO_FA = 1,
  NEW_IP = 2,
}

export type UtmData = { utm_user_uuid: string } & {
  [key in UtmParams]?: string;
};

export type MarkVisitRequest = Omit<UtmData, "gclid"> & {
  click_id?: string;
};

export interface TelegramWebappQueryData {
  path?: string;
  parameters?: Record<string, string>;
}

export interface WebAppUser {
  first_name: string;
  last_name?: string;
  user?: string;
  is_premium?: boolean;
  added_to_attachment_menu?: boolean;
  allows_write_to_pm?: boolean;
}

export interface ValidateWebAppInitDataRequest {
  init_data: string;
  source?: string;
  source_button?: string;
}

export type ValidateWebAppInitDataResponse = ApiResponse<{
  is_new_user: boolean;
}> &
  LoginResponse;

export interface CheckVersionData {
  message?: string;
  force_update?: boolean;
}
